import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode, User } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errorMessage } from "../../lib/api";
import { useLang } from "../../lib/i18n";

const SLOTS = [["header", "Top right", 1], ["primary", "Main (large)", 1], ["secondary", "Second row", 2], ["auxiliary", "Third row", 3], ["back", "Back of card", 12]];
const SAMPLE = { name_en: "Sara Ahmed", name_ar: "سارة أحمد", title_en: "Senior Designer", title_ar: "مصممة أولى", email: "sara@inception.sa",
  company: "Inception", card_link: "mycard.inception.sa/c/sara", phone: "+966 50 123 4567", department_en: "Design" };
const labelCls = "text-[11px] font-bold uppercase tracking-wider text-slate-500";

const Preview = ({ d, labelOf }) => {
  const custom = d.colors === "custom";
  const bg = custom ? d.bg : "#262626", fg = custom ? d.fg : "#ffffff", lb = custom ? d.label : "#ffffff";
  const F = ({ k, big }) => (k ? (
    <div className="min-w-0"><p className="text-[9px] font-semibold uppercase" style={{ color: lb }}>{labelOf(k)}</p>
      <p className={`truncate ${big ? "text-xl font-bold" : "text-sm"}`} style={{ color: fg }}>{SAMPLE[k] || "…"}</p></div>) : null);
  return (
    <div dir="ltr" className="w-full max-w-[340px] shrink-0 space-y-4 rounded-2xl p-4 shadow-xl" style={{ background: bg }} data-testid="wallet-preview">
      <div className="flex items-start justify-between">
        <img src={`/brand/inception-${!custom || parseInt(bg.slice(1, 3), 16) < 140 ? "white" : "black"}.png`} alt="" className="h-4 w-auto" />
        <F k={d.header} />
      </div>
      <div className="flex items-center justify-between gap-2"><F k={d.primary} big />
        {d.show_photo && <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white/20"><User className="h-6 w-6" style={{ color: fg }} /></span>}</div>
      <div className="flex gap-4">{d.secondary.map((k) => <F key={k} k={k} />)}</div>
      <div className="flex gap-4">{d.auxiliary.map((k) => <F key={k} k={k} />)}</div>
      <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-lg bg-white"><QrCode className="h-16 w-16 text-slate-900" /></div>
    </div>
  );
};

const Chips = ({ value, max, options, onChange, testId }) => {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : value.length < max ? [...value, k] : value);
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button key={o.key} type="button" onClick={() => toggle(o.key)} data-testid={`${testId}-${o.key}`}
          className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${value.includes(o.key) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{o.label}</button>
      ))}
    </div>
  );
};

export const WalletDesign = () => {
  const { t } = useLang();
  const [d, setD] = useState(null);
  const [options, setOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/wallet/design").then(({ data }) => { setD(data.design); setOptions(data.options); }).catch((e) => toast.error(errorMessage(e))); }, []);
  const set = (k) => (v) => setD((x) => ({ ...x, [k]: v }));
  const labelOf = (k) => options.find((o) => o.key === k)?.label || k;
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/settings/wallet/design", d); setD(data.design); toast.success(t("Wallet design saved")); }
    catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };

  if (!d) return <div className="h-48 animate-pulse rounded-3xl bg-slate-200" />;
  return (
    <div className="space-y-4 rounded-3xl border border-slate-200/80 bg-white p-6" data-testid="wallet-design">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto font-display text-xl font-bold text-slate-900">{t("Wallet card design")}</h2>
        <button type="button" onClick={save} disabled={saving} data-testid="wallet-design-save"
          className="btn-press min-h-[42px] rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white disabled:opacity-60">{saving ? t("Saving…") : t("Save design")}</button>
      </div>
      <div className="flex flex-col gap-6 lg:flex-row">
        <Preview d={d} labelOf={labelOf} />
        <div className="flex-1 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Switch checked={d.colors === "custom"} onCheckedChange={(v) => set("colors")(v ? "custom" : "card")} data-testid="wallet-custom-colors" />
              {t(d.colors === "custom" ? "Custom colours" : "Use each employee's card colour")}
            </label>
            {d.colors === "custom" && [["bg", "Background"], ["fg", "Text"], ["label", "Labels"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                <input type="color" value={d[k]} onChange={(e) => set(k)(e.target.value)} className="h-8 w-8 cursor-pointer rounded border-0 p-0" data-testid={`wallet-color-${k}`} /> {t(l)}
              </label>
            ))}
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Switch checked={d.show_photo} onCheckedChange={set("show_photo")} data-testid="wallet-show-photo" /> {t("Show photo")}
            </label>
          </div>
          {SLOTS.map(([slot, label, max]) => (
            <div key={slot} className="space-y-1.5">
              <p className={labelCls}>{t(label)} {max > 1 && <span className="normal-case text-slate-400">({t("up to {count}", { count: max })})</span>}</p>
              {max === 1 ? (
                <Select value={d[slot] || "none"} onValueChange={(v) => set(slot)(v === "none" ? "" : v)}>
                  <SelectTrigger className="h-10 w-64 rounded-xl" data-testid={`wallet-slot-${slot}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {slot === "header" && <SelectItem value="none">{t("None")}</SelectItem>}
                    {options.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : <Chips value={d[slot]} max={max} options={options} onChange={set(slot)} testId={`wallet-slot-${slot}`} />}
            </div>
          ))}
          <p className="text-xs text-slate-400">{t("Empty fields are skipped on each person's pass. Changes apply to passes downloaded after saving.")}</p>
        </div>
      </div>
    </div>
  );
};
